import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { lunchAPI } from '../services/api';

// Get menu for a specific date
export const useLunchMenu = (date: string) => {
  return useQuery({
    queryKey: ['lunch-menu', date],
    queryFn: () => lunchAPI.getMenu(date),
    enabled: !!date
  });
};

// Get today's menu
export const useTodayLunchMenu = () => {
  const today = new Date().toISOString().split('T')[0];
  return useLunchMenu(today);
};

// Create or update menu
export const useCreateLunchMenu = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: lunchAPI.createMenu,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['lunch-menu', variables.date] });
      queryClient.invalidateQueries({ queryKey: ['lunch-menu'] });
    }
  });
};

// Get employee's order for a specific date
export const useLunchOrder = (date: string) => {
  return useQuery({
    queryKey: ['lunch-order', date],
    queryFn: () => lunchAPI.getMyOrder(date),
    enabled: !!date
  });
};

// Create or update employee's order
export const useCreateLunchOrder = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: lunchAPI.createOrder,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['lunch-order', variables.date] });
      queryClient.invalidateQueries({ queryKey: ['lunch-orders'] });
    }
  });
};

// Delete employee's order
export const useDeleteLunchOrder = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: lunchAPI.deleteOrder,
    onSuccess: (_data, date) => {
      queryClient.invalidateQueries({ queryKey: ['lunch-order', date] });
      queryClient.invalidateQueries({ queryKey: ['lunch-orders'] });
    }
  });
};

// Get all orders for a specific date
export const useLunchOrders = (date: string) => {
  return useQuery({
    queryKey: ['lunch-orders', date],
    queryFn: () => lunchAPI.getOrders(date),
    enabled: !!date
  });
};

// Get monthly summary
export const useLunchSummary = (year: number, month: number) => {
  return useQuery({
    queryKey: ['lunch-summary', year, month],
    queryFn: () => lunchAPI.getSummary(year, month),
    enabled: !!year && !!month
  });
};

// Update paid status for monthly lunch payment
export const useUpdateLunchPaymentPaid = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ employeeId, year, month, paid }: { employeeId: string; year: number; month: number; paid: boolean }) =>
      lunchAPI.updatePaymentPaid(employeeId, year, month, paid),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['lunch-summary', variables.year, variables.month] });
    }
  });
};

// Get employee names
export const useEmployeeNames = () => {
  return useQuery({
    queryKey: ['employee-names'],
    queryFn: lunchAPI.getEmployeeNames
  });
};

// Get employee's order for a specific date
export const useMyOrder = (employeeId: string | undefined, date: string) => {
  return useQuery({
    queryKey: ['employee-lunch-order', employeeId, date],
    queryFn: () => lunchAPI.getEmployeeOrder(employeeId!, date),
    enabled: !!employeeId && !!date
  });
};

// Create or update order for any employee
export const useCreateAdminOrder = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: lunchAPI.createAdminOrder,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['employee-lunch-order', variables.employeeId, variables.date] });
      queryClient.invalidateQueries({ queryKey: ['lunch-orders', variables.date] });
    }
  });
};

// Delete employee's order
export const useDeleteAdminOrder = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ employeeId, date }: { employeeId: string; date: string }) => 
      lunchAPI.deleteAdminOrder(employeeId, date),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['employee-lunch-order', variables.employeeId, variables.date] });
      queryClient.invalidateQueries({ queryKey: ['lunch-orders', variables.date] });
    }
  });
};
